import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { trashApi } from '../lib/api';
import Button from '../components/ui/Button';
import { PriorityBadge, StatusBadge, TeamBadge } from '../components/ui/Badge';
import { TableSkeleton } from '../components/ui/Skeleton';
import { FormattedNotes } from '../components/ui/MarkdownNotes';
import { formatAssigneeNames, formatDate, formatDateTime } from '../lib/utils';

function formatEstimate(task: any) {
  const hours = Number(task.estimated_hours || 0);
  const minutes = Number(task.estimated_minutes || 0);
  if (hours === 0 && minutes === 0) return '-';
  return `${hours > 0 ? `${hours}h ` : ''}${minutes > 0 ? `${minutes}m` : ''}`.trim();
}

function sourceLabel(source: string) {
  return source === 'page' ? 'Page Task' : 'Sitewide Task';
}

export default function DeletedTasks() {
  const qc = useQueryClient();
  const [source, setSource] = React.useState('all');
  const [search, setSearch] = React.useState('');

  const params: Record<string, string> = {};
  if (source !== 'all') params.source = source;
  if (search.trim()) params.search = search.trim();

  const { data, isLoading } = useQuery({
    queryKey: ['trash-tasks', params],
    queryFn: () => trashApi.tasks(params),
  });

  const restoreMutation = useMutation({
    mutationFn: ({ taskSource, id }: { taskSource: string; id: number }) => trashApi.restore(taskSource, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trash-tasks'] });
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['weekly-plan'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Task restored');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to restore task'),
  });

  const tasks = data?.data || [];

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div
        className="bg-white rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-4"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E4E4E7' }}
      >
        <div>
          <h2 className="text-base font-bold text-[#18181B]">Deleted Tasks</h2>
          <p className="text-sm text-[#71717A] mt-0.5">Restore tasks that were deleted by mistake</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={source}
            onChange={event => setSource(event.target.value)}
            className="rounded-lg border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]"
          >
            <option value="all">All deleted</option>
            <option value="sitewide">Sitewide</option>
            <option value="page">Page tasks</option>
          </select>
          <div className="relative min-w-[260px]">
            <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A1A1AA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search deleted tasks..."
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="w-full rounded-lg border border-[#E4E4E7] bg-white py-2 pl-9 pr-3 text-sm text-[#18181B] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]"
            />
          </div>
        </div>
      </div>

      <div
        className="bg-white rounded-xl overflow-hidden"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E4E4E7' }}
      >
        {isLoading ? (
          <TableSkeleton rows={8} cols={8} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Task</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Assigned</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Status</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Due</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Estimate</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Deleted</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Action</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task: any, index: number) => (
                  <tr
                    key={`${task.source}-${task.id}`}
                    className="border-b border-[#F4F4F5]"
                    style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
                  >
                    <td className="px-4 py-3 max-w-[340px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-[#F4F4F5] px-2 py-0.5 text-xs font-semibold text-[#71717A]">
                          {sourceLabel(task.source)}
                        </span>
                        <span className="rounded bg-[#F4F4F5] px-2 py-0.5 text-xs font-semibold text-[#52525B]">
                          {task.website_name || 'Tkxel'}
                        </span>
                        <PriorityBadge priority={task.priority} />
                      </div>
                      <p className="mt-1 font-semibold text-[#18181B]">{task.name}</p>
                      {task.page_url && (
                        <a
                          href={task.page_url.startsWith('http') ? task.page_url : `https://tkxel.com${task.page_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block max-w-[300px] truncate font-mono text-xs text-blue-600 hover:underline"
                        >
                          {task.page_url}
                        </a>
                      )}
                      {task.notes && <FormattedNotes value={task.notes} compact tone="muted" className="mt-1" />}
                    </td>
                    <td className="px-4 py-3">
                      {formatAssigneeNames(task) ? (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-[#52525B]">{formatAssigneeNames(task)}</p>
                          {task.assigned_team && <TeamBadge team={task.assigned_team} />}
                          {task.follow_up_by && <p className="text-[11px] text-[#A1A1AA]">Follow-up: {task.follow_up_by}</p>}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-[#D4D4D8]">-</span>
                          {task.follow_up_by && <p className="text-[11px] text-[#A1A1AA]">Follow-up: {task.follow_up_by}</p>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={task.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-[#71717A]">{formatDate(task.due_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-[#52525B]">{formatEstimate(task)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-[#71717A]">
                      <p>{formatDateTime(task.deleted_at)}</p>
                      <p className="mt-0.5 text-[#A1A1AA]">by {task.deleted_by || 'Unknown'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        loading={restoreMutation.isPending}
                        onClick={() => restoreMutation.mutate({ taskSource: task.source, id: Number(task.id) })}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v1M3 10l5 5m-5-5l5-5" />
                        </svg>
                        Restore
                      </Button>
                    </td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center text-sm text-[#A1A1AA]">
                      No deleted tasks found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
